import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { finalize } from 'rxjs/operators';

import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ContentTypesEnum } from '@enums/content-type.enum';
import { BaggageContent } from '@shared/services/content/_interfaces_/content.interface';
import { BaggageContentMapped } from '@shared/services/content/_interfaces_/content.mapped.interface';
import { ContentMapperService } from '@shared/services/content/content-mapper.service';
import { ContentService } from '@shared/services/content/content.service';
import { ExtrasInfo } from '@shared/services/extras/_interfaces_/extras-info.interface';
import { ExtrasOverviewService } from '../../../shared/services/extras/extras-overview.service';
import { ExtrasFormEnum } from '@enums/extras-form.enum';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { DateFormatEnum } from '@enums/date-format.enum';
import { ExtrasTypesEnum } from '@enums/extras-types';
import { PassengerConstant } from '@constants/partials/passengers.constans';

@Component({
  selector: 'app-baggage-accordion',
  templateUrl: './baggage-accordion.component.html',
  styleUrls: ['./baggage-accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaggageAccordionComponent implements OnInit, OnDestroy {
  private contentService = inject(ContentService);
  private contentMapperService = inject(ContentMapperService);
  private cd = inject(ChangeDetectorRef);
  private extrasOverviewService = inject(ExtrasOverviewService);
  private localStorageService = inject(LocalStorageService);

  @Input() extrasInfo: ExtrasInfo;
  @Input() culture: string;
  @Input() expandExtras: string;
  @Input() extrasTypesEnum = ExtrasTypesEnum;
  @Output() baggageSelectionChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  private unsubscribeAll$: Subject<void> = new Subject();
  isBaggageAvailable: boolean = false;
  flightIdentifier: string = '';
  baggageForm: FormGroup;
  loading: boolean = false;
  loadingIndex: number | null = null;
  ddMMyyyy: string = DateFormatEnum.ddMMyyyy;
  defaultPlaceholderReference: string = "NOBAG"

  initForm(): void {
    const passangersArrayForm = new FormArray([]);
    if (this.extrasInfo?.passengers?.length > 0) {
      this.extrasInfo.passengers.forEach((passenger) => {
        passangersArrayForm.push(
          new FormGroup({
            [ExtrasFormEnum.TravellerIdentifier]: new FormControl(passenger.passengerIdentifier ?? ''),
            [ExtrasFormEnum.BaggageOptionCode]: new FormControl(passenger.passengerExtras[0]?.options[0]?.bookingReference ?? ''),
            isInfant: new FormControl(passenger.passengerIdentifier === PassengerConstant.PASSENGER_INFANT),
          })
        );
      });
    }
    this.baggageForm = new FormGroup({
      passengersBaggage: passangersArrayForm,
    });
  }

  constructor() {}

  ngOnInit(): void {
    this.isBaggageAvailable = this.checkBaggageAvailability();
    this.initForm();
    this.flightIdentifier = this.extrasInfo?.flightIdentifier;
  }

  checkBaggageAvailability(): boolean {
    if (this.extrasInfo) {
      for (let passenger of this.extrasInfo.passengers) {
        if (passenger?.passengerExtras?.filter((extras) => extras.type === ExtrasTypesEnum.Baggage)[0]?.options?.length > 0) {
          return true;
        }
      }
      return false;
    }
  }

  checkBaggageSelection(): void {
    const isSelected = this.extrasInfo?.passengers?.some((passenger) =>
      passenger.passengerExtras[0]?.options?.some((option) => option.isSelected && option.pieces !== 0)
    );
    this.baggageSelectionChanged.emit(isSelected);
  }

  isAnyBaggageSelected(): boolean {
    return this.extrasInfo?.passengers?.some((passenger) =>
      passenger.passengerExtras[0]?.options?.some(
        (option) => option.isSelected && option.pieces !== 0
      )
    );
  }

  readonly contentBaggage$: Observable<BaggageContentMapped> = this.contentService
    .getContent<BaggageContent>(ContentTypesEnum.myFfExtrasPage)
    .pipe(map((content: BaggageContent) => this.contentMapperService.mapBaggageContent(content)));

  selectBaggage(passengerIdentifier: string, index: number): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.loadingIndex = index;

    const { baggageOptionCode, travellerIdentifier } = this.baggageForm.value.passengersBaggage.find(
      (passenger) => passenger[ExtrasFormEnum.TravellerIdentifier] === passengerIdentifier
    );

    this.extrasOverviewService
      .putUpdateBaggage(this.flightIdentifier, travellerIdentifier, baggageOptionCode)
      .pipe(
        take(1),
        takeUntil(this.unsubscribeAll$),
        catchError(() => {
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
          this.loadingIndex = null;
          this.cd.markForCheck();
        })
      )
      .subscribe((data) => {
        this.extrasInfo = data;
        this.extrasOverviewService.setExtrasData(data);
        this.checkBaggageSelection();
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
}
